import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    subsetRemover,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { Vardplatsbestallning } from "@/tabs/Vardplatsbokning/types";
import cloneDeep from "lodash/cloneDeep";

type VardplatsSparradTid = {
    id: number;
    startdatum: string;
    starttid: string;
    slutdatum: string;
    sluttid: string;
    sang: {
        id: number;
    };
};

type State = BaseState & {
    vardplatsSparradeTider: Map<string | number, Vardplatsbestallning>;
};

type Commit = (
    action: string,
    payload: unknown,
    options?: {
        root?: boolean;
    }
) => void;

type Getters = {
    vardplatsSparradTidById: (data: { id: number | string }) => Vardplatsbestallning | null;
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        vardplatsSparradeTider: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "vardplatsSparradeTider";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Den här ska inte anropas direkt. Hämtning ska göras via actionen (fetchVardplatsSparradeTider)
         */
        vardplatsSparradeTider: (state: State) => [...state.vardplatsSparradeTider.values()],
        /**
         * Anropar en generisk getter för hämtning av subset. computed i en komponent som lyssnar på denna getter
         * är förmodligen vad du vill ha i din komponent.
         * Se helpers/baseSubset
         */
        vardplatsSparradeTiderSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: moduleName,
            }),
        /**
         * Om du bara vill hämta ett objekt vid ett enda tillfälle så ska det göras via actionen och inte här
         * (getVardplatsbestallningById, som hämtar från BE om objektet inte finns i store).
         * Om du vill lyssna på förändringar på ett enskilt objekt så vill du troligen lyssna på den här i en
         * computed i din komponent
         */
        vardplatsSparradTidById:
            (state: State) =>
            ({ id }: { id?: number | string }) => {
                if (!id) {
                    return null;
                }

                return state.vardplatsSparradeTider.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        /**
         * Tillägg av nya objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (createVardplatsbestallning)
         * Se helpers/subset
         */
        addVardplatsSparradeTider: (
            state: State,
            {
                offset,
                subsetName = "default",
                vardplatsSparradeTider = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                vardplatsSparradeTider: VardplatsSparradTid[];
            }
        ) =>
            subsetSetter<State, VardplatsSparradTid>({
                offset,
                subsetName,
                state,
                collectionName: moduleName,
                items: vardplatsSparradeTider,
            }),
        /**
         * Uppdatering av objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (updateVardplatsbestallning)
         * Se helpers/subset
         */
        updateVardplatsSparradeTider: (
            state: State,
            {
                vardplatsSparradeTider,
                offset,
                subsetName,
            }: {
                vardplatsSparradeTider: VardplatsSparradTid[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, VardplatsSparradTid>({
                offset,
                state,
                collectionName: moduleName,
                items: vardplatsSparradeTider,
            }),
        deleteVardplatsSparradTider: (
            state: State,
            {
                ids,
            }: {
                ids: number[];
            }
        ) =>
            subsetRemover({
                ids,
                state,
                collectionName: moduleName,
            }),
    },
    /**
     * Actions ska alltid innehålla baseActions + det som är specifikt för modulen. Den returnerar resultatet,
     * men det du troligen vill göra är att lyssna på
     */
    actions: {
        ...baseActions({ moduleName }),
        getVardplatsSparradTidById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
                addAsNew,
                addAsUpdate,
            }: {
                id: number | string;
                // Ska hämtade datan stoppas in i newlyCreated subset?
                addAsNew?: boolean;
                /*
                    Ska hämtningen läggas till som newlyUpdated?
                    Existerande data kommer att ignoreras i store och en ny hämtning från BE kommer att göras
                 */
                addAsUpdate?: boolean;
            }
        ) => {
            const foundTid = getters.vardplatsSparradTidById({ id });

            if (!addAsUpdate && (foundTid || !id)) {
                if (foundTid && addAsNew) {
                    commit("addVardplatsSparradeTider", {
                        offset: 0,
                        vardplatsSparradeTider: [foundTid],
                        subsetName: "newlyCreated",
                    });
                }

                return foundTid;
            }

            try {
                const { data } = await klinikenApi.get<VardplatsSparradTid>(
                    `slutenvard/sparradetider/${id}`,
                    getConfig({})
                );

                if (data) {
                    commit("addVardplatsSparradeTider", {
                        offset: 0,
                        vardplatsSparradeTider: [data],
                        subsetName: addAsNew ? "newlyCreated" : id,
                    });

                    if (addAsUpdate) {
                        commit("updateVardplatsSparradeTider", {
                            subsetName: "newlyUpdated",
                            vardplatsSparradeTider: [data],
                            offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                        });
                    }

                    return data;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        /**
         * Hämta slutenvårdsbesök från state eller från backend och stoppa in i state. Den returnerar inte resultatet.
         * Det du troligen vill göra är att skapa en computed som lyssnar på subsetgettern.
         * Den kapar ett nytt subset om inskickade filteringParams inte redan finns i state.
         * @param limit mängden objekt du vill få tillbaka i resultatet
         * @param page nummer för page. Används för att räkna ut offset som skickas till BE
         * @param filterParams alla sök och filtreringsparametrar. "ordering" är för sortering
         */
        fetchVardplatsSparradeTider: async (
            { commit, state }: BaseActionsProps<State>,
            {
                forceRefresh,
                resetSubset,
                resetNewlyCreated,
                limit = 20,
                page = 1,
                filterParams,
            }: {
                limit: number;
                page: number;
                filterParams: FilterParams;
                forceRefresh?: boolean;
                resetSubset?: boolean;
                /*
                    Ska subset med nyligen skapade rensas? Kan användas i t.ex listor där användaren
                    gör en ny filtrering eller manuell refresh och vi inte vill att nyligen skapade ska fortsätta visas
                 */
                resetNewlyCreated?: boolean;
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, VardplatsSparradTid>({
                    filterParams,
                    state,
                    limit,
                    page,
                    forceRefresh,
                    collectionName: moduleName,
                    apiUrl: "slutenvard/sparradetider/",
                });

                if (resetNewlyCreated) {
                    commit("addVardplatsSparradeTider", {
                        offset: 0,
                        subsetName: "newlyCreated",
                        resetSubset: true,
                        slutenvardsbesok: [],
                    });
                }

                if (!results) {
                    return {
                        // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                        subsetName, // Totala mängden träffar från sökningen
                        total, // Nummer för nästa page
                        nextPage,
                    };
                }

                commit("addVardplatsSparradeTider", {
                    offset,
                    subsetName,
                    vardplatsSparradeTider: results,
                });

                return {
                    // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                    subsetName, // Totala mängden träffar från sökningen
                    total, // Nummer för nästa page
                    nextPage,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createVardplatsSparradTid: async (
            { commit, state, dispatch }: BaseActionsProps<State>,
            {
                vardplatsSparradTid,
            }: {
                vardplatsSparradTid: VardplatsSparradTid;
            }
        ) => {
            try {
                const response = await klinikenApi.post(
                    "slutenvard/sparradetider/",
                    vardplatsSparradTid,
                    getConfig({})
                );

                const data = await dispatch("getVardplatsSparradTidById", {
                    id: response.data.id,
                    addAsNew: true,
                });

                commit("addVardplatsSparradeTider", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    vardplatsSparradeTider: [data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateVardplatsSparradTid: async (
            { commit, state, dispatch }: BaseActionsProps<State>,
            {
                vardplatsSparradTid,
            }: {
                vardplatsSparradTid: VardplatsSparradTid;
            }
        ) => {
            try {
                const response = await klinikenApi.put(
                    `slutenvard/sparradetider/${vardplatsSparradTid.id}/`,
                    vardplatsSparradTid,
                    getConfig({})
                );

                await dispatch("getVardplatsSparradTidById", {
                    id: response.data.id,
                    addAsUpdate: true,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        deleteVardplatsSparradTid: async (
            { commit }: BaseActionsProps<State>,
            {
                vardplatsSparradTid,
            }: {
                vardplatsSparradTid: VardplatsSparradTid;
            }
        ) => {
            try {
                await klinikenApi.delete(`slutenvard/sparradetider/${vardplatsSparradTid.id}/`, getConfig({}));

                commit("deleteVardplatsSparradTider", { ids: [vardplatsSparradTid.id] });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
